import { useEffect } from 'react'
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx'
import { useTranslation } from 'react-i18next'
import { Theme } from '@/components/theme-provider.tsx'
import { Button } from '@/components/ui/button.tsx'
import { CalendarSync, HelpCircle, LogOut, UserPlus, UserRoundCog } from 'lucide-react'
import usePreferences from '@/usePreferences.ts'
import { useTheme } from '@/useTheme.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { Badge } from '@/components/ui/badge.tsx'
import { Browser } from '@capacitor/browser'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { dispatch } from '@/useBus.ts'
import dayjs from 'dayjs'
import PrivacyEulaLinks from '@/components/privacy-eula-links.tsx'

const PageSettings = ({ logOutUser }: { logOutUser: () => void }) => {
	const { t } = useTranslation()
	const { user, isAuthLoaded, appLanguageCode, changeAppLanguage, availableAppLanguages } = useGlobalStore()
	const { appPreferencesLoaded } = usePreferences()
	const { appTheme, setAppTheme } = useTheme()

	useEffect(() => {
		if (appTheme) {
			if (appTheme === 'system' && !appPreferencesLoaded) return
			setAppTheme(appTheme as Theme, true)
		}
	}, [appTheme, appPreferencesLoaded, setAppTheme])

	const handleLanguageChange = (newLanguageCode: string) => {
		if ((newLanguageCode === null || newLanguageCode === '') && !appPreferencesLoaded) return
		changeAppLanguage(newLanguageCode).then()
	}

	const openUserProfileSettingsPage = async () => {
		if (user) {
			await Browser.open({
				url: `${import.meta.env.VITE_APP_URL}/app/gateway?redirect=profile.show&app_language_code=${appLanguageCode}&email=${user.email}&auth=${user?.access_token}`,
			})
		}
	}

	const openHelpAndSupportPage = async () => {
		await Browser.open({
			url: 'https://lingoflow.app/support',
		})
	}

	return (
		<DrawerContent
			dir={availableAppLanguages[appLanguageCode]?.dir}
			aria-describedby="App settings drawer"
			className="top-0 m-0 ml-[20%] md:ml-[70%] rounded-t-none border-0 pt-safe">
			<VisuallyHidden.Root>
				<DrawerDescription>App Settings</DrawerDescription>
				<div>App Settings</div>
			</VisuallyHidden.Root>
			<DrawerHeader>
				<DrawerTitle className="mb-2 text-md">
					<div className="font-semibold">{t('Welcome')}</div>
					<div className="font-light">
						{user?.name !== null && user?.name !== undefined ? `${user?.name}!` : ''}
					</div>
				</DrawerTitle>
				<div className="flex justify-between items-center">
					{isAuthLoaded && (
						<>
							{user?.trial_expires_at !== null && dayjs.utc(user?.trial_expires_at) > dayjs().utc() ? (
								<Badge
									onClick={() => dispatch('show-auth-dialog')}
									className="flex justify-center cursor-pointer min-h-7 rounded-md text-xs font-medium flex-col"
									variant="outline">
									{t('Unregistered trial')}
								</Badge>
							) : user?.subscription_expires_at !== null &&
							  dayjs.utc(user?.subscription_expires_at) > dayjs().utc() ? (
								<Badge
									onClick={() => dispatch('toggle-manage-subscription-dialog', true)}
									className="flex justify-center cursor-pointer h-7 rounded-md text-xs font-bold flex-col diagonal-stripes border-0 text-black">
									{t('Unlimited Access')}
								</Badge>
							) : (
								<Badge
									onClick={() =>
										dispatch('toggle-purchase-subscription-dialog', {
											val: true,
											mode: 'maximum',
										})
									}>
									{t('Free Account')}
								</Badge>
							)}
						</>
					)}
					<div>
						{user !== null ? (
							<div className="flex">
								<Button
									onClick={openUserProfileSettingsPage}
									variant="ghost"
									size="icon"
									className="h-7">
									<UserRoundCog className="h-4 w-4" />
								</Button>
								<Button onClick={logOutUser} variant="ghost" size="icon" className="h-7 -me-3">
									<LogOut className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<Button onClick={logOutUser} variant="secondary" size="icon" className="h-7 px-2 w-auto">
								<UserPlus className="h-4 w-4 me-2" />
								<span>{t('Sign In')}</span>
							</Button>
						)}
					</div>
				</div>
			</DrawerHeader>
			<Separator />
			<div className="h-full break-all overflow-y-auto p-4 [touch-action:pan-y]">
				<div className="w-full space-y-6">
					<div className="space-y-4">
						<div>
							<label className="block text-sm pb-2 font-medium text-gray-700 dark:text-gray-400">
								{t('Color Scheme')}
							</label>
							<Select onValueChange={(val) => setAppTheme(val as Theme, true)} value={appTheme}>
								<SelectTrigger>
									<SelectValue placeholder="Select the app theme" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="system">{t('System (auto)')}</SelectItem>
									<SelectItem value="light">{t('Light')}</SelectItem>
									<SelectItem value="dark">{t('Dark')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<label className="block text-sm pb-2 font-medium text-gray-700 dark:text-gray-400">
								{t('App Language')}
							</label>
							<p className="text-sm pb-2 text-muted-foreground break-keep break-normal">
								{t(
									'Used for all AI messages and translations. Choose your native tongue if available.',
								)}
							</p>
							<Select onValueChange={handleLanguageChange} value={appLanguageCode}>
								<SelectTrigger>
									<SelectValue placeholder="Select the app interface language" />
								</SelectTrigger>
								<SelectContent>
									{Object.entries(availableAppLanguages).map(([key, value]) => (
										<SelectItem value={key} key={key}>
											{value.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{user?.subscription_expires_at !== null &&
							dayjs.utc(user?.subscription_expires_at) > dayjs().utc() && (
								<div>
									<Button
										className="w-full"
										onClick={() => dispatch('toggle-manage-subscription-dialog', true)}
										variant="secondary">
										<CalendarSync className="w-4 h-4 me-2" />
										{t('Manage subscription')}
									</Button>
								</div>
							)}
						<div>
							<Button className="w-full" onClick={openHelpAndSupportPage} variant="secondary">
								<HelpCircle className="w-4 h-4 me-2" />
								{t('Help and support')}
							</Button>
						</div>
						<PrivacyEulaLinks className="pt-2" />
					</div>
				</div>
			</div>
		</DrawerContent>
	)
}

export default PageSettings
